import { useState } from 'react';
import type { ScheduleSpec } from '../api/types';

const WEEKDAYS = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

export function specToCron(spec: ScheduleSpec, rawCron: string): string {
  switch (spec.kind) {
    case 'interval':
      return `*/${Math.max(1, spec.minutes)} * * * *`;
    case 'daily': {
      const [h, m] = spec.time.split(':');
      return `${Number(m)} ${Number(h)} * * *`;
    }
    case 'weekly': {
      const [h, m] = spec.time.split(':');
      return `${Number(m)} ${Number(h)} * * ${spec.weekday}`;
    }
    case 'monthly': {
      const [h, m] = spec.time.split(':');
      return `${Number(m)} ${Number(h)} ${Math.min(31, Math.max(1, spec.day))} * *`;
    }
    case 'advanced':
      return rawCron;
  }
}

interface Props {
  spec: ScheduleSpec | null;
  cron: string;
  onChange: (cron: string, spec: ScheduleSpec) => void;
}

export default function CronBuilder({ spec, cron, onChange }: Props) {
  const [local, setLocal] = useState<ScheduleSpec>(spec ?? { kind: 'daily', time: '09:00' });
  const [raw, setRaw] = useState(cron || '0 9 * * *');

  function emit(next: ScheduleSpec, nextRaw = raw) {
    setLocal(next);
    setRaw(nextRaw);
    onChange(specToCron(next, nextRaw), next);
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 text-sm">
        {(['interval', 'daily', 'weekly', 'monthly', 'advanced'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              const defaults: Record<string, ScheduleSpec> = {
                interval: { kind: 'interval', minutes: 30 },
                daily: { kind: 'daily', time: '09:00' },
                weekly: { kind: 'weekly', weekday: 1, time: '09:00' },
                monthly: { kind: 'monthly', day: 1, time: '09:00' },
                advanced: { kind: 'advanced' },
              };
              emit(defaults[k]);
            }}
            className={`px-2 py-1 rounded border ${
              local.kind === k ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-300'
            }`}
          >
            {{ interval: 'Intervalo', daily: 'Diário', weekly: 'Semanal', monthly: 'Mensal', advanced: 'Cron avançado' }[k]}
          </button>
        ))}
      </div>

      {local.kind === 'interval' && (
        <label className="block text-sm">
          A cada{' '}
          <input
            type="number"
            min={1}
            className="input w-20 inline-block"
            value={local.minutes}
            onChange={(e) => emit({ kind: 'interval', minutes: Number(e.target.value) })}
          />{' '}
          minuto(s)
        </label>
      )}

      {local.kind === 'daily' && (
        <label className="block text-sm">
          Todo dia às{' '}
          <input
            type="time"
            className="input w-32 inline-block"
            value={local.time}
            onChange={(e) => emit({ kind: 'daily', time: e.target.value })}
          />
        </label>
      )}

      {local.kind === 'weekly' && (
        <div className="text-sm space-y-2">
          <div className="flex flex-wrap gap-1">
            {WEEKDAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => emit({ kind: 'weekly', weekday: d.value, time: local.time })}
                className={`px-2 py-1 rounded border ${
                  local.weekday === d.value ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-300'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <label className="block">
            às{' '}
            <input
              type="time"
              className="input w-32 inline-block"
              value={local.time}
              onChange={(e) => emit({ kind: 'weekly', weekday: local.weekday, time: e.target.value })}
            />
          </label>
        </div>
      )}

      {local.kind === 'monthly' && (
        <div className="text-sm space-y-1">
          <label className="block">
            Todo dia{' '}
            <input
              type="number"
              min={1}
              max={31}
              className="input w-20 inline-block"
              value={local.day}
              onChange={(e) => emit({ kind: 'monthly', day: Number(e.target.value), time: local.time })}
            />{' '}
            de cada mês, às{' '}
            <input
              type="time"
              className="input w-32 inline-block"
              value={local.time}
              onChange={(e) => emit({ kind: 'monthly', day: local.day, time: e.target.value })}
            />
          </label>
          <p className="text-xs text-slate-500">Dias 29–31 são pulados nos meses que não os têm.</p>
        </div>
      )}

      {local.kind === 'advanced' && (
        <label className="block text-sm">
          Expressão cron (5 campos: min hora dia mês dia-semana)
          <input
            type="text"
            className="input font-mono mt-1"
            value={raw}
            placeholder="0 9 * * 1"
            onChange={(e) => emit({ kind: 'advanced' }, e.target.value)}
          />
        </label>
      )}

      <p className="text-xs text-slate-500 font-mono">cron: {specToCron(local, raw)}</p>
    </div>
  );
}
